import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';

export const useUsers = (params = {}) => {
  return useQuery(
    ['users', params],
    () => userService.getUsers(params),
    {
      keepPreviousData: true,
      staleTime: 30000 // 30 seconds
    }
  );
};

export const useUser = (id) => {
  return useQuery(
    ['user', id],
    () => userService.getUser(id),
    {
      enabled: !!id,
      staleTime: 60000 // 1 minute
    }
  );
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation(userService.createUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('User created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) => userService.updateUser(id, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['user', variables.id]);
        queryClient.invalidateQueries('users');
        toast.success('User updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user');
      }
    }
  );
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation(userService.deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('User deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  });
};

export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, isActive }) => userService.updateUserStatus(id, isActive),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['user', variables.id]);
        queryClient.invalidateQueries('users');
        toast.success(`User ${variables.isActive ? 'activated' : 'deactivated'} successfully`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user status');
      }
    }
  );
};

export const useUserPermissions = (userId) => {
  return useQuery(
    ['user-permissions', userId],
    () => userService.getUserPermissions(userId),
    {
      enabled: !!userId,
      staleTime: 300000 // 5 minutes
    }
  );
};

export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, permissions }) => userService.updateUserPermissions(id, permissions),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['user-permissions', variables.id]);
        queryClient.invalidateQueries(['user', variables.id]);
        toast.success('User permissions updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user permissions');
      }
    }
  );
};

export const useUserStats = (userId) => {
  return useQuery(
    ['user-stats', userId],
    () => userService.getUserStats(userId),
    {
      enabled: !!userId,
      staleTime: 60000 // 1 minute
    }
  );
};

export const useUsersByRole = (role) => {
  return useQuery(
    ['users-by-role', role],
    () => userService.getUsersByRole(role),
    {
      enabled: !!role,
      staleTime: 300000 // 5 minutes
    }
  );
};

export const useUsersByBranch = (branchId) => {
  return useQuery(
    ['users-by-branch', branchId],
    () => userService.getUsersByBranch(branchId),
    {
      enabled: !!branchId,
      staleTime: 300000 // 5 minutes
    }
  );
};

export const useResetUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation(userService.resetUserPassword, {
    onSuccess: () => {
      toast.success('Password reset email sent successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reset user password');
    }
  });
};

export const useBulkUpdateUsers = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ userIds, updateData }) => userService.bulkUpdateUsers(userIds, updateData),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('users');
        toast.success(`${variables.userIds.length} users updated successfully`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update users');
      }
    }
  );
};